'use strict';

// Import custom event polyfill, as this is not polyfilled by babel 🙃
import 'custom-event-polyfill';

import { CONST, addEvent } from 'app_vodafone_ziggo/util/util';

// Util/Helpers
import 'app_vodafone_ziggo/util/drag';
import { addHistoryListeners } from './util/history';
import { handleError } from './util/responseHandling.js';
import { keyboardNavigation } from './util/navigation';
import { goToStep, skipStep, pdLinkHandler } from './util/spa';

// Components
import 'app_vodafone_ziggo/VZCarousel';
import 'app_vodafone_ziggo/components/fade-in';
import 'app_vodafone_ziggo/components/prompt';
import 'app_vodafone_ziggo/components/searchCompany';

import { handleQuestionKeyUp } from './components/form';
import { ProgressIndicator } from './components/progressIndicator';
import './components/modal.js';
import { initKonamiCode } from './components/konami';

// Step specific code
import { initStartPage } from './steps/startPage';

// Constants
const selectors = {
    mainContainer: '.js-bscan-main',
    step: '.js-bscan-step',
    statementsStep: '.js-bscan-step.bscan-statements',
    stepInput: '.js-bscan-step-input',
    companyListItem: '.js-search-company-results .js-btn-next',
    statement: '.js-bscan-statement',
    statementsSubmit: '.js-bscan-statement .js-btn-next',
    goToAdviceDetail: '.js-bscan-advice-detail',
    goToThankYou: '.js-bscan-to-thankyou',
    productIDs: '.js-product-ids',
    avatar: '.js-bscan-step-avatar',
    allAnswersField: '.js-bscan-answers',
    button: {
        backToAdvice: '.js-back-to-advice',
        back: '.js-btn-back',
        close: '.js-btn-close',
        next: '.js-btn-next',
        pdButton: '.bscan-pd .btn',
        skipQuestion: '.js-btn-skip-question',
        quit: '.js-btn-quit'
    },
    scrollArea: '.bscan-step-scroll-area',
    errorModal: '#general-error',
    groupedQuestions: '.js-grouped-question'
};

const attributes = {
    baseUrl: 'data-bscan-url',
    bsFlow: 'data-bscan-flow',
    delay: 'data-bscan-delay',
    disableUrlUpdate: 'data-bscan-disable-url-update',
    endpoint: 'data-bscan-endpoint',
    endpointQuestion: 'data-bscan-endpoint-question',
    currentStep: 'data-bscan-current-step',
    targetStep: 'data-bscan-step-target',
    stepId: 'data-bscan-step-id',
    stepIndex: 'data-bscan-step-index',
    stepAnswer: 'data-bscan-step-answer',
    stepName: 'data-bscan-step-name',
    stepProgIndicator: 'data-bscan-prog-indicator',
    statementId: 'data-bscan-statement-id',
    statementName: 'data-bscan-statement-name',
    videoID: 'data-video-id',
    groupedQuestionId: 'data-bscan-grouped-question-id',
    skippedQuestions: 'data-skipped-questions',
    companyInfo: {
        address: 'data-company-address',
        city: 'data-company-city',
        id: 'data-company-id',
        kvk: 'data-company-kvk',
        name: 'data-company-name'
    }
};

const classes = {
    pageTransition: 'page-transition',
    introAnimation: 'intro-animation',
    adviceAnimation: 'advice-animation',
    skipped: 'skipped',
    stepStart: 'bscan-start',
    stepQuestion: 'bscan-question',
    stepQuestionGroup: 'bscan-question-group',
    stepCompanySearch: 'bscan-search-company',
    statements: {
        container: 'bscan-statements'
    },
    advicePages: 'bscan-advice-pages'
};

const values = {
    skipped: 'skipped'
};

class BusinessScan {
    constructor() {
        this.selectors = selectors;
        this.attributes = attributes;
        this.classes = classes;
        this.values = values;
        this.CONST = CONST;
        this.givenAnswers = {};

        this.baseTitle = document.title;
        this.mainContainer = document.querySelector(selectors.mainContainer);
        this.isBsFlow = this.mainContainer.hasAttribute(attributes.bsFlow);
        this.baseUrl = this.mainContainer && this.mainContainer.getAttribute(attributes.baseUrl);
        this.btnBack = document.querySelector(selectors.button.back);
        this.progressIndicator = new ProgressIndicator();

        this.stepEvent = 'bscan:step';
        this.firstStep = document.querySelector(selectors.step);
        this.firstStepIndex = this.firstStep.getAttribute(attributes.stepIndex);
        this.startStepIndex = '-1';

        this.currentStep = null;
        this.currentStepId = null;
        this.currentStepName = null;
        this.currentStepIndex = null;
        this.currentStepInput = null;
        this.currentStepAnswer = null;
        this.currentStepNextButton = null;

        this.targetStep = null;
        this.targetStepId = null;
        this.targetStepIndex = null;
        this.targetStepName = null;

        this.goingBack = null;
        this.pageTransitionDuration = null;
        this.lastHistorySequence = null;
        this.historyChange = false;
        this.historySequence = 0;
        this.stepsOrder = [this.firstStepIndex];

        this.groupedQuestions = {};

        initStartPage.call(this);
        addHistoryListeners.call(this);
        initKonamiCode();

        document.addEventListener(CONST.events.keyUp, keyboardNavigation.bind(this));
        document.addEventListener(CONST.events.responseError, handleError.bind(this));

        addEvent(document, CONST.events.submit, `${selectors.step}.${CONST.classes.active}`, goToStep.bind(this));
        addEvent(
            document,
            CONST.events.click,
            `
            ${selectors.button.back},
            ${selectors.companyListItem},
            ${selectors.statementsSubmit},
            ${selectors.goToAdviceDetail}
        `,
            goToStep.bind(this)
        );
        addEvent(document, CONST.events.click, `${selectors.button.skipQuestion}`, skipStep.bind(this));
        addEvent(document, CONST.events.click, `${selectors.button.pdButton}`, pdLinkHandler.bind(this));
        addEvent(document, CONST.events.keyUp, selectors.stepInput, handleQuestionKeyUp.bind(this));
        addEvent(document, CONST.events.change, selectors.stepInput, handleQuestionKeyUp.bind(this));
    }
}

(() => new BusinessScan())();
